<script setup lang="ts">
import { computed } from '#imports';
import { PerfectScrollbar as PScrollbar } from 'vue3-perfect-scrollbar';
import PerfectScrollbar from 'perfect-scrollbar';
import useResponsive from '@/shared/composables/use-responsive';

const props = withDefaults(defineProps<{
	options?: PerfectScrollbar.Options;
	areRailsVisible?: boolean;
	isWheelPropagation?: boolean;
}>(), {
	options: () => ({}),
	areRailsVisible: false,
	isWheelPropagation: false,
});

const emit = defineEmits<{
	(e: 'y-reach-start'): void;
	(e: 'y-reach-end'): void;
	(e: 'ps-scroll-up'): void;
	(e: 'ps-scroll-down'): void;
}>();

const { isTablet, isLaptop } = useResponsive();

const scrollOptions = computed(() => {
	const handlers = ['keyboard', 'wheel', 'drag-thumb'];

	if (!isLaptop.value) {
		handlers.push('touch');
	}

	return {
		wheelPropagation: !isTablet.value ? true : props.isWheelPropagation,
		...props.options,
		handlers,
	};
});
</script>

<template>
	<p-scrollbar
		:is="$attrs.tag || 'div'"
		:options="scrollOptions"
		class="scrollbar"
		:class="{ 'scrollbar--rails-visible': areRailsVisible }"
		@ps-y-reach-start="emit('y-reach-start')"
		@ps-y-reach-end="emit('y-reach-end')"
		@ps-scroll-up="emit('ps-scroll-up')"
		@ps-scroll-down="emit('ps-scroll-down')"
	>
		<slot />
	</p-scrollbar>
</template>

<style lang="scss">
.scrollbar {
	$scrollbar-thumb-thickness: $gap-micro;
	$scrollbar-thumb-offset: $gap-micro;

	position: relative;

	overflow: hidden;

	touch-action: none;

	&--rails-visible {

		.ps__rail-x,
		.ps__rail-y {
			display: block;

			opacity: 1 !important;
		}
	}

	.ps__rail-x,
	.ps__rail-y {
		background-color: #ecf0f4 !important;
	}

	.ps__rail-x.ps--clicking {
		.ps__thumb-x {
			height: rem($scrollbar-thumb-thickness);
		}
	}

	.ps__rail-y.ps--clicking {
		.ps__thumb-y {
			width: rem($scrollbar-thumb-thickness);
		}
	}

	.ps__rail-y {
		left: initial !important;

		width: rem(6px);
		margin-top: rem($scrollbar-thumb-offset);
		margin-bottom: rem($scrollbar-thumb-offset);
		padding-right: rem(1px);
		padding-left: rem(1px);

		&:hover {
			.ps__thumb-y {
				width: rem($scrollbar-thumb-thickness);
			}
		}
	}

	.ps__rail-x {
		height: rem(6px);
		margin-right: rem($scrollbar-thumb-offset);
		margin-left: rem($scrollbar-thumb-offset);
		padding-top: rem(1px);
		padding-bottom: rem(1px);

		&:hover {
			.ps__thumb-x {
				height: rem($scrollbar-thumb-thickness);
			}
		}
	}

	.ps__thumb-x,
	.ps__thumb-y {
		background-color: $dusty-gray !important;
	}

	.ps__thumb-x {
		bottom: initial;

		height: rem($scrollbar-thumb-thickness);
	}

	.ps__thumb-y {
		right: initial;

		width: rem($scrollbar-thumb-thickness);
	}
}
</style>
